// AccessKeyModal.js
import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Modal, Form, Input, Button } from "antd";
import axios from 'axios';
import config from "../config";
import Swal from 'sweetalert2';
import CloseButton from "../components/CloseButton";

const AccessKeyModal = forwardRef((props, ref) => {
  // Null check: if props is null, render nothing (but after all hooks)
  const apiBaseUrl = config.REACT_APP_API_BASE_URL;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolvePromise, setResolvePromise] = useState(null);
  const [form] = Form.useForm();
  const inputRef = React.useRef(null);

  // Helper to trigger keyboard on mobile and desktop browsers
  const focusInputAndKeyboard = () => {
    // try {
      if (inputRef.current) {
      //   // Remove and re-add the input to force iOS Safari to show the keyboard
        const el = inputRef.current;
          el.focus();
          // For iOS Safari, Android Chrome, and desktop Chrome/Safari
          const ua = navigator.userAgent;
          const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
          const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
          const isChrome = /chrome|crios/i.test(ua);
          if (typeof window !== 'undefined' && (isMobile || isSafari || isChrome)) {
            el.click && el.click();
            setTimeout(() => {
              el.focus();
            }, 300);
          }
      }

  };

  useImperativeHandle(ref, () => ({
    show: () => {
      setOpen(true);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.blur(); // Ensure input is not focused before modal opens
        }
      }, 300);
      setTimeout(() => {
        focusInputAndKeyboard();
      }, 300); // Delay to ensure modal is fully open before focusing
      return new Promise((resolve) => {
        setResolvePromise(() => resolve);
      });
    },
  }));

  const handleCancel = () => {
    form.resetFields();
    setOpen(false);
    // Blur the input to hide the keyboard immediately on modal close (especially for Safari)
    if (inputRef.current) {
      inputRef.current.blur();
    }
    resolvePromise(null); // If cancelled, return null
  };

  const handleFinish = async ({ accessKey }) => {
    setLoading(true);

    try {
      // Simulate API call (replace with actual API call)
      const response = await axios.post(`${apiBaseUrl}/api/users/get-user-by-access-key`, {
      accessKey,
    });

      if (response) {
        setOpen(false);
        form.resetFields();
        resolvePromise(response.data); 
      }
    } catch (error) {
      setOpen(false);
      form.resetFields();
      Swal.fire({
        icon: 'error',
        title: 'Invalid Access Key',
        toast: true, 
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
              });
      resolvePromise(null);
    } finally {
      setLoading(false);
    }
  };

  if (props == null) return null;

  return (
    <Modal
      title={
      <div>
        Enter Access Key
        <CloseButton onClick={() => handleCancel()} />
        </div>
                  }
      open={open}
      footer={null}
      onCancel={handleCancel}
      destroyOnClose
      className="access-key-modal"
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="accessKey"
          label="Access Key"
          rules={[
            { required: true, message: "Please enter the access key" },
            { pattern: /^\d{4}$/, message: "Must be 4 digits" },
          ]}
        >
          {(() => {
            const isIpad = typeof navigator !== 'undefined' && /iPad/.test(navigator.userAgent);
            if (isIpad) {
              return (
                <input
                  ref={inputRef}
                  type="password"
                  maxLength={4}
                  inputMode="numeric"
                  autoFocus
                  style={{ width: '100%', padding: 8, fontSize: 16, borderRadius: 4, border: '1px solid #d9d9d9' }}
                  onFocus={e => {
                    e.target.removeAttribute('readonly');
                    if (typeof e.target.setSelectionRange === 'function') {
                      e.target.setSelectionRange(0, e.target.value.length);
                    }
                  }}
                  onTouchStart={e => {
                    e.target.setAttribute('readonly', true);
                    setTimeout(() => {
                      e.target.removeAttribute('readonly');
                      e.target.focus();
                    }, 50);
                  }}
                />
              );
            } else {
              return (
                <Input.Password
                  ref={inputRef}
                  maxLength={4}
                  inputMode="numeric"
                  autoFocus
                  onFocus={e => {
                    if (typeof window !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                      e.target.removeAttribute('readonly');
                      if (e && e.target && typeof e.target.setSelectionRange === 'function') {
                        e.target.setSelectionRange(0, e.target.value.length);
                      }
                      setTimeout(() => {
                        e.target.click && e.target.click();
                        e.target.focus();
                      }, 10);
                    }
                  }}
                  onTouchStart={e => {
                    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
                      e.target.setAttribute('readonly', true);
                      setTimeout(() => {
                        e.target.removeAttribute('readonly');
                        e.target.focus();
                      }, 50);
                    }
                  }}
                />
              );
            }
          })()}
        </Form.Item>
        <div style={{ textAlign: "right" }}>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </div>
      </Form>
    </Modal>
  );
});

export default AccessKeyModal;
